import React from 'react';
import { Button, Dropdown, message as antMessage, Modal } from 'antd';
import type { MenuProps } from 'antd';
import { ArrowLeftOutlined, ClearOutlined, EditOutlined, LogoutOutlined, MoreOutlined } from '@ant-design/icons';
import { Avatar } from 'components/elements';
import ChatGroupModal from 'components/elements/chatGroupModal';
import { useChat } from './context/ChatContext';
import MessageList from './MessageList';
import Composer from './Composer';
import {
  ChatBackButton,
  ChatHeader,
  ChatHeaderLeft,
  ChatHeaderAvatarWrap,
  ChatHeaderDetail,
  ChatHeaderInfo,
  ChatHeaderName,
  ChatHeaderOnlineDot,
  ChatHeaderStatusText,
  ChatViewPanel,
} from './chat.styled';

interface ChatViewProps {
  onBack?: () => void;
}

const ChatView: React.FC<ChatViewProps> = ({ onBack }) => {
  const {
    user,
    availableUsers,
    activeConversation,
    sortedMessages,
    messagesLoading,
    messageInput,
    setMessageInput,
    handleSendMessage,
    messagesContainerRef,
    onlineUserIds,
    clearingHistory,
    leavingGroup,
    handleClearHistory,
    handleLeaveGroup,
    handleEditGroup,
  } = useChat();

  const [editGroupModalOpen, setEditGroupModalOpen] = React.useState(false);

  if (!activeConversation) return null;

  const title = activeConversation.is_group
    ? activeConversation.name ?? 'Group chat'
    : activeConversation.other_username ?? 'Chat';

  const isOnline =
    !activeConversation.is_group && activeConversation.other_user_id
      ? onlineUserIds.includes(activeConversation.other_user_id)
      : false;

  const groupMembersCount = activeConversation.is_group
    ? activeConversation.member_ids?.length ?? 0
    : 0;

  const actionMenuItems: MenuProps['items'] = [
    { key: 'clear-history', icon: <ClearOutlined />, label: 'Clear History' },
    ...(activeConversation.is_group
      ? [
        { type: 'divider' } as const,
        {
          key: 'edit-group',
          icon: <EditOutlined />,
          label: 'Edit Group',
        } as const,
        { type: 'divider' } as const,
        {
          key: 'leave-group',
          icon: <LogoutOutlined />,
          label: 'Delete and Leave',
          danger: true,
        } as const,
      ]
      : []),
  ];

  const onActionMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'clear-history') {
      Modal.confirm({
        title: 'Clear chat history',
        content: 'This will permanently remove all messages in this conversation.',
        okText: 'Clear',
        cancelText: 'Cancel',
        okButtonProps: { danger: true, loading: clearingHistory },
        onOk: async () => {
          try {
            await handleClearHistory();
            antMessage.success('Chat history cleared');
          } catch {
            antMessage.error('Failed to clear chat history');
          }
        },
      });
      return;
    }

    if (key === 'edit-group') {
      setEditGroupModalOpen(true);
      return;
    }

    if (key === 'leave-group') {
      Modal.confirm({
        title: 'Leave group',
        content: 'You will stop receiving messages from this group.',
        okText: 'Leave',
        cancelText: 'Cancel',
        okButtonProps: { danger: true, loading: leavingGroup },
        onOk: async () => {
          try {
            await handleLeaveGroup();
            antMessage.success('You left the group');
          } catch {
            antMessage.error('Failed to leave group');
          }
        },
      });
    }
  };

  return (
    <ChatViewPanel>
      <ChatHeader>
        <ChatHeaderLeft>
          {onBack && (
            <ChatBackButton onClick={onBack} aria-label="Back to chats">
              <ArrowLeftOutlined />
            </ChatBackButton>
          )}
          <ChatHeaderInfo>
            <ChatHeaderAvatarWrap>
              <Avatar
                avatarUrl={activeConversation.other_avatar_url ?? undefined}
                size="xsm"
              />
              {!activeConversation.is_group && isOnline && (
                <ChatHeaderOnlineDot $online={isOnline} />
              )}
            </ChatHeaderAvatarWrap>
            <ChatHeaderDetail>
              <ChatHeaderName>{title}</ChatHeaderName>
              {!activeConversation.is_group && (
                <ChatHeaderStatusText $online={isOnline}>
                  {isOnline ? 'Online' : 'Offline'}
                </ChatHeaderStatusText>
              )}
              {activeConversation.is_group && (
                <ChatHeaderStatusText $online={false}>
                  {groupMembersCount} member{groupMembersCount === 1 ? '' : 's'}
                </ChatHeaderStatusText>
              )}
            </ChatHeaderDetail>
          </ChatHeaderInfo>
        </ChatHeaderLeft>
        <Dropdown
          placement="bottomRight"
          trigger={['click']}
          menu={{ items: actionMenuItems, onClick: onActionMenuClick }}
        >
          <Button
            type="primary"
            icon={<MoreOutlined />}
            loading={clearingHistory || leavingGroup}
          />
        </Dropdown>
      </ChatHeader>

      <MessageList
        messages={sortedMessages}
        messagesLoading={messagesLoading}
        currentUserId={user?.id ?? 0}
        isGroup={activeConversation.is_group}
        messagesContainerRef={messagesContainerRef}
      />

      <Composer
        value={messageInput}
        onChange={setMessageInput}
        onSend={handleSendMessage}
      />

      {activeConversation.is_group && (
        <ChatGroupModal
          modalVisible={editGroupModalOpen}
          setModal={setEditGroupModalOpen}
          contacts={(availableUsers ?? []).map((u) => ({
            id: u.id,
            username: u.username,
            avatarUrl: u.avatarUrl,
            group: 0,
          }))}
          initialValues={{
            id: String(activeConversation.id),
            name: activeConversation.name ?? '',
            members: (activeConversation.member_ids ?? []).map(String),
            editing: true,
          }}
          onCreateGroup={async (payload) => {
            try {
              await handleEditGroup({
                id: String(activeConversation.id),
                name: payload.name,
                members: payload.members,
              });
              setEditGroupModalOpen(false);
              antMessage.success('Group updated successfully');
            } catch {
              antMessage.error('Failed to update group');
            }
          }}
        />
      )}
    </ChatViewPanel>
  );
};

export default ChatView;
